import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { listQuerySchema } from './pagination';
import { JOB_STATUS_VALUES, type JobStatusValue } from './job';
import type { ChangeRequestView } from './change-request';

/*
 * What the client portal sends and what it gets back. ROADMAP.md §5,
 * reference/business-rules.md §2.
 *
 * **These views are built, never borrowed.** There is no path from an admin view
 * builder to this file, and that is the whole design: `client_note` is the
 * office's notebook about the customer, `satisfaction_rating` is internal
 * (question 8), an employee's pay rate is admin-only, and another client's
 * anything is nobody's. The seam that cannot leak is the one that never reads
 * the column — same reasoning as `toEmployeeView` in 3.4.
 *
 * So every interface here is a deliberate list of what a client may read. Adding
 * a field is a decision somebody makes; it is not something that happens because
 * a shared type grew one.
 */

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

/**
 * The client's own account, as they see it.
 *
 * Read-only by default — question 10, and the reason is that an address must not
 * change under a job that is already scheduled. `recoveryEmail` is the exception
 * and 5.7 makes it settable: an account whose login is a username has no other
 * way back in (P9).
 */
export interface ClientProfileView {
  /** Business name when there is one, otherwise the person. Never a slug. */
  displayName: string;
  contactName: string;

  /** The client record's contact details — what the office rings and writes to. */
  email: string | null;
  phone: string | null;

  customerSince: string | null;

  /** The login itself. One of these two is always present. */
  loginEmail: string | null;
  username: string | null;

  /** Where a password reset goes when the login is a username. */
  recoveryEmail: string | null;
}

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

/**
 * One of the client's own addresses.
 *
 * `accessNotes` is here on purpose: business-rules.md §2 gives a client their
 * own access notes in full. It is their front door.
 */
export interface ClientPropertyPortalView {
  id: string;
  nickname: string | null;
  isPrimary: boolean;

  address: string;
  city: string;
  state: string;
  zip: string;
  fullAddress: string;

  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;

  categoryId: string | null;
  /** The display name, never the id or a slug (P5). */
  categoryName: string | null;

  specialInstructions: string | null;
  accessNotes: string | null;

  isActive: boolean;
}

/*
 * **There is no separate property schema for the portal**, deliberately. A
 * client owns their address as fully as the office does (business-rules.md §2,
 * "Properties · own, full CRUD"), so the input is the admin's
 * `clientPropertySchema` unchanged — two schemas over one table is two sets of
 * length limits and validation messages that drift apart.
 *
 * What the portal does not accept is a `clientId`. Which client a property
 * belongs to comes from the session and from nowhere else, so it was never a
 * field on that schema in the first place.
 */

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

/** A crew member, by full name — question 11's default, matching the prototype. */
export interface ClientCrewMember {
  name: string;
  /** "Lead cleaner" or "Cleaner". Humanised here, never the stored role. */
  roleLabel: string;
}

export interface ClientJobListItem {
  id: string;
  jobNumber: string;

  status: JobStatusValue;
  statusLabel: string;

  serviceTypeName: string;
  categoryName: string | null;

  scheduledDate: string;
  scheduledTime: string;
  estimatedDurationMinutes: number;

  addressSnapshot: string;
  propertyNickname: string | null;

  crew: ClientCrewMember[];
}

export interface ClientJobTaskView {
  id: string;
  name: string;
  isCompleted: boolean;
  /** Surfaced to the client in the prototype too — FINDINGS.md §10.3. */
  photoCount: number;
}

export interface ClientJobPhotoView {
  id: string;
  caption: string | null;
  /** "Before", "After", "Issue" — humanised, never the stored value. */
  kindLabel: string;
  uploadedByName: string;
  uploadedAt: string;
  /** The client-scoped app URL. The stored key never leaves the server. */
  url: string;
}

export interface ClientJobDetail extends ClientJobListItem {
  specialInstructions: string | null;

  /** Why the office cancelled it. Their job, so their answer. */
  cancelledReason: string | null;

  startedAt: string | null;
  completedAt: string | null;

  property: ClientPropertyPortalView | null;

  tasks: ClientJobTaskView[];
  photos: ClientJobPhotoView[];

  taskCounts: {
    total: number;
    completed: number;
  };

  /**
   * Whether the client may ask us to move or cancel it — 5.6. False once the
   * cleaner has started, because a job in progress cannot be rescheduled.
   */
  canRequestChange: boolean;

  /** The one they have already sent about this clean, if it is still waiting. */
  openChangeRequest: ChangeRequestView | null;
}

export const clientJobListQuerySchema = listQuerySchema.extend({
  status: z.enum([...JOB_STATUS_VALUES, 'all']).default('all'),
});

export type ClientJobListQuery = z.infer<typeof clientJobListQuerySchema>;

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

/**
 * Everything the landing screen needs, in one response.
 *
 * One call rather than four, because a dashboard that renders in four stages is
 * a dashboard that flickers — and because the counts and the list are answers to
 * the same question ("what is going on with my cleans"), so they should not be
 * able to disagree with each other by arriving a second apart.
 */
export interface ClientDashboard {
  profile: ClientProfileView;

  /** Zero is `0` on every one of these, never a dash (P1). */
  counts: {
    upcoming: number;
    completed: number;
    properties: number;
    /** Requests still waiting on the office — 5.4. */
    openRequests: number;
  };

  /** The next few cleans, soonest first. */
  upcoming: ClientJobListItem[];

  /** Where the client's post goes when the office writes to them. */
  primaryAddress: string | null;
}

// ---------------------------------------------------------------------------
// Shared display helpers
// ---------------------------------------------------------------------------

/** Never render the stored role. design-system.md §7, defect P5. */
export const CREW_ROLE_LABELS: Record<'lead' | 'cleaner' | 'support', string> = {
  lead: 'Lead cleaner',
  cleaner: 'Cleaner',
  support: 'Support',
};

/** Never render the stored kind. */
export const PHOTO_KIND_LABELS: Record<'before' | 'after' | 'issue', string> = {
  before: 'Before',
  after: 'After',
  issue: 'Issue',
};

export { calendarDateSchema };

// ---------------------------------------------------------------------------
// Profile and notification preferences — ROADMAP.md §5.7
// ---------------------------------------------------------------------------

/**
 * Setting the address a password reset goes to.
 *
 * **This is the other half of P9.** The prototype has live accounts whose email
 * column holds the literal string `client`: they cannot reset a password and
 * cannot be contacted. `user_contactable_chk` has enforced the pairing since
 * 2.1, and 5.7 is the first screen where somebody can actually set one.
 */
export const recoveryEmailSchema = z.object({
  recoveryEmail: z.email('Enter a valid email address').max(255),
});

export type RecoveryEmailInput = z.infer<typeof recoveryEmailSchema>;

/**
 * The types a client is offered — data-model.md §8, role-scoped.
 *
 * `job_reminder` joined in 11.2, which is the slice that made reminders real:
 * a preference for a notification nothing sends is a lie on a settings screen,
 * so the type and the worker task that raises it arrived together.
 */
export const CLIENT_NOTIFICATION_TYPES = [
  'job_scheduled',
  'request_declined',
  'cleaning_complete',
  'job_reminder',
] as const;

export type ClientNotificationTypeValue = (typeof CLIENT_NOTIFICATION_TYPES)[number];

/**
 * Display names. **"Request Declined"** — one word for the state, not the
 * prototype's "Request Not Approved" (P11, P18).
 */
export const CLIENT_NOTIFICATION_TYPE_LABELS: Record<ClientNotificationTypeValue, string> = {
  job_scheduled: 'Job Scheduled',
  request_declined: 'Request Declined',
  cleaning_complete: 'Cleaning Complete',
  job_reminder: 'Cleaning Reminder',
};

/** What each one actually means, in the customer's language. */
export const CLIENT_NOTIFICATION_TYPE_HINTS: Record<ClientNotificationTypeValue, string> = {
  job_scheduled: 'When we confirm a date for a clean you asked for.',
  request_declined: 'When we cannot do a clean you asked for, and why.',
  cleaning_complete: 'When your cleaner has finished and the photos are up.',
  job_reminder: 'The evening before a clean, so tomorrow is not a surprise.',
};

export const notificationPreferenceSchema = z.object({
  type: z.enum(CLIENT_NOTIFICATION_TYPES),
  channel: z.enum(['in_app', 'email', 'push']),
  isEnabled: z.boolean(),
});

export type NotificationPreferenceInput = z.infer<typeof notificationPreferenceSchema>;

export interface ClientNotificationPreferences {
  /** Keyed `type:channel`. **Absent means on** — see the migration. */
  disabled: string[];
}
